// Keeps <link rel="canonical"> honest per route.
//
// index.html ships a single canonical pointing at the homepage, which is right for
// the app itself (every gated route is a duplicate of the shell as far as a crawler
// is concerned). It is wrong for the handful of public marketing pages: /download,
// /privacy and /terms were self-canonicalling to "/", so Google treated them as
// duplicates and dropped them, which contradicted sitemap.xml listing them as own URLs.
//
// Anything not in the allowlist keeps pointing at "/" on purpose. That is what stops
// app routes, promo paths and query-string variants from splitting into thin duplicates.

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const ORIGIN = 'https://www.timohelps.com';

// Public pages that deserve to be indexed under their own URL. Keep this in sync with
// the non-article entries in public/sitemap.xml.
// /pricing is deliberately absent: it is behind ProtectedRoute and Disallow-ed in
// robots.txt, so it must not advertise itself as an indexable page.
const SELF_CANONICAL = new Set([
  '/',
  '/download',
  '/privacy',
  '/terms',
]);

function normalise(pathname) {
  if (!pathname) return '/';
  // Trailing slashes would otherwise produce /download and /download/ as two canonicals.
  const trimmed = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return trimmed === '' ? '/' : trimmed;
}

export default function CanonicalTag() {
  const location = useLocation();

  useEffect(() => {
    const path = normalise(location.pathname);
    const target = SELF_CANONICAL.has(path) ? `${ORIGIN}${path === '/' ? '/' : path}` : `${ORIGIN}/`;

    let tag = document.querySelector('link[rel="canonical"]');
    if (!tag) {
      tag = document.createElement('link');
      tag.setAttribute('rel', 'canonical');
      document.head.appendChild(tag);
    }
    if (tag.getAttribute('href') !== target) {
      tag.setAttribute('href', target);
    }

    // og:url should agree with the canonical or unfurlers pick the wrong one.
    const og = document.querySelector('meta[property="og:url"]');
    if (og && og.getAttribute('content') !== target) {
      og.setAttribute('content', target);
    }
  }, [location.pathname]);

  return null;
}
